import styled from 'styled-components';

export const RacesContent = styled.div`
  margin-top: 24px;
`;

export const EmptyMessage = styled.div`
  text-align: center;
  padding: 48px 20px;
  font-size: 16px;
  color: ${({ theme }) => theme.colors.text.body};
`;

export const RacesList = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 600px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 8px;
  }
`;

export const RaceCard = styled.div<{ $gradient?: string; $isCompleted?: boolean; $isPending?: boolean }>`
  background: ${({ $gradient, $isCompleted, $isPending, theme }) => {
    if ($isCompleted || $isPending) {
      return theme.colors.background.secondary;
    }
    return $gradient || 'linear-gradient(45deg, rgba(6, 6, 6, 1) 0%, rgba(0, 71, 187, 1) 100%)';
  }};
  border-radius: 4px;
  padding: 12px;
  transition: transform 0.2s;
  display: flex;
  flex-direction: column;
  position: relative;

  &:hover {
    transform: translateY(-4px);
  }
`;

export const StatusBadge = styled.div<{ $status: 'active' | 'pending' | 'completed' }>`
  position: absolute;
  top: 12px;
  right: 12px;
  padding: 4px 6px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  
  ${({ $status, theme }) => {
    if ($status === 'active') {
      return `
        color: #47D7AC;
        background-color: rgba(71, 215, 172, 0.1);
      `;
    } else if ($status === 'pending') {
      return `
        color: ${theme.colors.text.alwaysWhite};
        background-color: ${theme.colors.transparent.light10};
      `;
    } else {
      return `
        color: #FBAE27;
        background-color: rgba(251, 174, 39, 0.1);
      `;
    }
  }}
`;

export const DateBadge = styled.div`
  position: absolute;
  top: 15px;
  left: 12px;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text.alwaysWhite};
`;

export const RaceImage = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 34px;
  border-radius: 8px;

  img {
    width: 130px;
    height: 100px;
    object-fit: contain;
    filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.3));
  }
`;

export const RaceInfo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 85px;
  margin-bottom: 18px;
  gap: 8px;
`;

export const RaceName = styled.h3`
  font-size: 16px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.text.title};
  margin: 0;
  text-align: center;
`;

export const JackpotLabel = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;

  svg {
    fill: ${({ theme }) => theme.colors.icon.chevron};
  }
`;

export const JackpotLabelInfoIcon = styled.div`
  display: flex;
  align-items: center;
  cursor: pointer;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.7;
  }
`;

export const JackpotLabelInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.body};
  line-height: 0;
`;

export const JackpotValue = styled.div`
  font-size: 20px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.alwaysWhite};
`;

export const RaceStats = styled.div<{ $isPending?: boolean }>`
  background-color: ${({ theme }) => theme.colors.transparent.light5};
  border-radius: 4px;
  display: grid;
  grid-template-columns: ${({ $isPending }) => $isPending ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)'};
  height: 54px;
  margin-bottom: 8px;
  padding: 8px;

  ${({ $isPending }) => $isPending ? `
    & > :first-child {
      border-right: 1px solid rgba(255, 255, 255, 0.05);
    }
  ` : `
    & > :nth-child(2) {
      border-left: 1px solid rgba(255, 255, 255, 0.05);
      border-right: 1px solid rgba(255, 255, 255, 0.05);
    }
  `}
`;

export const RaceStat = styled.div<{ $iconColor?: string }>`
  align-items: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 4px;
  font-size: 13px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.text.alwaysWhite};

  svg {
    width: 18px;
    height: 18px;
    flex-shrink: 0;
    fill: ${({ $iconColor, theme }) => $iconColor || theme.colors.icon.chevron};
  }
`;

export const RaceTimer = styled.div`
  align-items: center;
  display: flex;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 12px;
`;

export const TimerLabel = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.subtitle};
  text-align: center;
`;

export const TimerValues = styled.div`
  display: flex;
  justify-content: center;
  gap: 4px;
`;

export const TimerUnit = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
`;

export const TimerValue = styled.div`
  background-color: ${({ theme }) => theme.colors.transparent.light10};
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.text.alwaysWhite};
  text-align: center;
  height: 24px;
  width: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const TimerUnitLabel = styled.div`
  font-size: 16px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.text.body};
`;

export const TimerSeparator = styled.div`
  font-size: 16px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.text.alwaysWhite};
  margin-top: 2px;
`;

export const RaceActions = styled.div`
  display: flex;
  gap: 8px;
  justify-content: flex-end;
`;

